import React, { useState, useEffect } from 'react';
import SmartUploadSection from './SmartUploadSection';
import SmartResultsSection from './SmartResultsSection';
import FraudDetectionCard from './FraudDetectionCard';
import { getFullApiUrl } from '../config';
import { addAuthHeader } from '../utils/auth';

const SmartParserContent = ({ onProcessingStateChange }) => {
  const [tableResults, setTableResults] = useState([]);
  const [fraudAnalysis, setFraudAnalysis] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [recentResults, setRecentResults] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  const handleSmartProcessingComplete = (results, fraudData) => {
    setTableResults(results);
    setFraudAnalysis(fraudData);
    setIsDataLoaded(true);
  };

  // Load recent results
  useEffect(() => {
    loadRecentResults();
  }, []);

  // Notify parent about processing state
  useEffect(() => {
    if (onProcessingStateChange) {
      onProcessingStateChange(isProcessing);
    }
  }, [isProcessing, onProcessingStateChange]);

  const loadRecentResults = async () => {
    setLoadingHistory(true);
    try {
      const baseUrl = getFullApiUrl('SMART_PARSER').replace('/process-files', '');
      const response = await fetch(`${baseUrl}/history?limit=5`, {
        headers: addAuthHeader({})
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRecentResults(result.results || []);
        }
      }
    } catch (error) {
      console.error('Error loading recent results:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleLoadResult = async (resultId) => {
    try {
      const baseUrl = getFullApiUrl('SMART_PARSER').replace('/process-files', '');
      const response = await fetch(`${baseUrl}/history/${resultId}`, {
        headers: addAuthHeader({})
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.result) {
          setTableResults(result.result.transactions || []);
          setFraudAnalysis(result.result.fraud_analysis);
          setIsDataLoaded(true);
          
          // Scroll to results
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (error) {
      console.error('Error loading result:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditTitle = (e, result) => {
    e.stopPropagation();
    setEditingTitle(result.id);
    setEditTitleValue(result.title || `Parsing ${result.id}`);
  };

  const handleKeyPress = (e, resultId) => {
    if (e.key === 'Enter') {
      handleSaveTitle(resultId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleCancelEdit = () => {
    setEditingTitle(null);
    setEditTitleValue('');
  };

  const handleSaveTitle = async (resultId) => {
    if (!editTitleValue.trim()) {
      alert('Title tidak boleh kosong');
      return;
    }

    setIsSavingTitle(true);
    try {
      const baseUrl = getFullApiUrl('SMART_PARSER').replace('/process-files', '');
      const response = await fetch(`${baseUrl}/history/${resultId}/title`, {
        method: 'PATCH',
        headers: addAuthHeader({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({ title: editTitleValue.trim() })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Update local state
          setRecentResults(prev => prev.map(r => 
            r.id === resultId ? { ...r, title: result.result.title } : r
          ));
          setEditingTitle(null);
          setEditTitleValue('');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Gagal mengupdate title');
      }
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Gagal mengupdate title');
    } finally {
      setIsSavingTitle(false);
    }
  };

  return (
    <main className="p-6">
      <div className="space-y-8 pb-20">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2 transition-colors duration-300">PDF Parser Smart</h1>
          <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
            Parser bank statement berbasis AI untuk ekstraksi transaksi otomatis dari berbagai format file
          </p>
        </div>

        {/* Features Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-3 transition-colors duration-300">
          <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-3 flex items-center transition-colors duration-300">
            <i className="fa-solid fa-star mr-2 text-yellow-500 dark:text-yellow-400 text-xs"></i>
            Key Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
                <i className="fa-solid fa-robot text-primary-600 dark:text-primary-400 text-xs transition-colors duration-300"></i>
              </div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">AI-Powered Parsing</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
                Menggunakan teknologi OCR dan AI untuk ekstraksi transaksi yang akurat
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
                <i className="fa-solid fa-files text-blue-600 dark:text-blue-400 text-xs transition-colors duration-300"></i>
              </div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Multiple File Support</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
                Upload beberapa file sekaligus (PDF, JPG, JPEG, PNG) untuk diproses dalam batch
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 dark:bg-green-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
                <i className="fa-solid fa-shield-halved text-green-600 dark:text-green-400 text-xs transition-colors duration-300"></i>
              </div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Fraud Detection</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
                Deteksi potensi penipuan dan anomali transaksi secara otomatis
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 dark:bg-purple-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
                <i className="fa-solid fa-table text-purple-600 dark:text-purple-400 text-xs transition-colors duration-300"></i>
              </div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Structured Data</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
                Hasil ekstraksi dalam format tabel yang mudah dianalisis dan diekspor
              </p>
            </div>
          </div>
        </div>

        {/* Warning Banner when Processing */}
        {isProcessing && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-500 p-4 rounded-lg transition-colors duration-300">
            <div className="flex items-center">
              <i className="fa-solid fa-exclamation-triangle text-yellow-600 dark:text-yellow-400 mr-3 transition-colors duration-300"></i>
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 transition-colors duration-300">
                  Proses parsing sedang berjalan
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 transition-colors duration-300">
                  Harap tetap berada di halaman ini sampai proses selesai. Jika Anda meninggalkan halaman ini, hasil parsing mungkin akan hilang.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Section */}
        <SmartUploadSection 
          onProcessingComplete={handleSmartProcessingComplete}
          onProcessingChange={setIsProcessing}
        />
        
        {/* Results Section */}
        {isDataLoaded && (
          <>
            <FraudDetectionCard fraudAnalysis={fraudAnalysis} />
            <SmartResultsSection 
              tableResults={tableResults} 
              isDataLoaded={isDataLoaded}
            />
          </>
        )}

        {/* Recently Parsed Section */}
        {!isDataLoaded && recentResults.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center transition-colors duration-300">
                <i className="fa-solid fa-clock-rotate-left mr-2 text-primary-600 dark:text-primary-400"></i>
                Recently Parsed
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">
                Parsing yang pernah dilakukan oleh anggota group Anda
              </p>
            </div>
            <div className="p-6">
              {loadingHistory ? (
                <div className="text-center py-8">
                  <i className="fa-solid fa-spinner fa-spin text-2xl text-gray-400 dark:text-gray-500"></i>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 transition-colors duration-300">Memuat history...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentResults.map((result) => (
                    <div
                      key={result.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors cursor-pointer"
                      onClick={() => handleLoadResult(result.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center mb-2">
                          <i className="fa-solid fa-file-invoice text-primary-600 mr-2"></i>
                          {editingTitle === result.id ? (
                            <div className="flex items-center flex-1 min-w-0">
                              <input
                                type="text"
                                value={editTitleValue}
                                onChange={(e) => setEditTitleValue(e.target.value)}
                                onKeyDown={(e) => handleKeyPress(e, result.id)}
                                onBlur={(e) => {
                                  // Delay to allow button clicks
                                  setTimeout(() => {
                                    if (document.activeElement !== e.target) {
                                      handleCancelEdit();
                                    }
                                  }, 200);
                                }}
                                autoFocus
                                className="flex-1 px-2 py-1 text-sm font-medium text-gray-800 dark:text-white bg-white dark:bg-gray-700 border border-primary-500 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300"
                                disabled={isSavingTitle}
                                maxLength={255}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveTitle(result.id);
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={isSavingTitle}
                                className="ml-2 px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                title="Simpan"
                              >
                                <i className={`fa-solid ${isSavingTitle ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={isSavingTitle}
                                className="ml-1 px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white text-xs rounded transition-colors"
                                title="Batal"
                              >
                                <i className="fa-solid fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center flex-1 min-w-0">
                              <h3 className="font-medium text-gray-800 dark:text-white truncate transition-colors duration-300">
                                {result.title || `Parsing ${result.id}`}
                              </h3>
                              <button
                                onClick={(e) => handleEditTitle(e, result)}
                                className="ml-1.5 p-1 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition-colors flex-shrink-0"
                                title="Edit title"
                              >
                                <i className="fa-solid fa-pen text-xs"></i>
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
                          <span>
                            <i className="fa-solid fa-user mr-1"></i>
                            {result.creator_name || result.creator_username || 'Unknown'}
                          </span>
                          <span>
                            <i className="fa-solid fa-calendar mr-1"></i>
                            {formatDate(result.created_at)}
                          </span>
                          <span>
                            <i className="fa-solid fa-list mr-1"></i>
                            {result.total_transactions || 0} transaksi
                          </span>
                          {result.files_processed > 0 && (
                            <span>
                              <i className="fa-solid fa-file mr-1"></i>
                              {result.files_processed} file
                            </span>
                          )}
                        </div>
                        {result.files && result.files.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {result.files.map((file, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700"
                              >
                                <i className="fa-solid fa-file-pdf mr-1"></i>
                                {file.original_filename}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadResult(result.id);
                        }}
                        className="ml-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors flex items-center"
                      >
                        <i className="fa-solid fa-eye mr-2"></i>
                        Load
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Help Section */}
        {!isDataLoaded && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 transition-colors duration-300">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
              <i className="fa-solid fa-question-circle mr-2 text-primary-600 dark:text-primary-400"></i>
              How to Use
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">1. Upload Files:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                  <li>• Klik tombol "Upload & Analyze" atau drag & drop file ke area upload</li>
                  <li>• Format yang didukung: PDF, JPG, JPEG, PNG</li>
                  <li>• Maksimal ukuran file: 50MB per file</li>
                  <li>• Bisa upload beberapa file sekaligus untuk diproses dalam batch</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">2. AI Processing:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                  <li>• Sistem akan melakukan Optical Character Recognition (OCR) untuk membaca teks</li>
                  <li>• Teknologi AI akan menganalisis dan mengekstrak transaksi bank</li>
                  <li>• Fraud detection akan mendeteksi anomali dan potensi penipuan</li>
                  <li>• Proses biasanya memakan waktu beberapa detik hingga menit</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">3. Review Results:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                  <li>• Lihat hasil ekstraksi transaksi dalam format tabel</li>
                  <li>• Periksa fraud detection analysis untuk transaksi mencurigakan</li>
                  <li>• Data dapat diekspor untuk analisis lebih lanjut</li>
                  <li>• Upload file baru untuk analisis tambahan</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">4. Tips & Best Practices:</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                  <li>• Pastikan file bank statement jelas dan tidak blur</li>
                  <li>• Untuk hasil terbaik, gunakan PDF yang dapat dibaca (bukan scan berkualitas rendah)</li>
                  <li>• File dengan banyak halaman mungkin membutuhkan waktu lebih lama</li>
                  <li>• Periksa hasil ekstraksi dan bandingkan dengan dokumen asli</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default SmartParserContent; 